import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, ScrollView, TouchableOpacity, TextInput, ActivityIndicator } from 'react-native';
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../config/firebase';
import { useAdminThemeContext } from '../../contexts/AdminThemeContext';
import { BASE_LOCATION_CATEGORIES } from '../../constants/locationCategories';
import { getAdminSearchPlaceholderColor, setWebPlaceholderColor } from '../../utils/setWebPlaceholderColor';

const DENY_REASON_INPUT_ID = 'admin-location-deny-reason';

function formatTime(ts) {
  if (!ts) return '—';
  try {
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  } catch {
    return '—';
  }
}

export function AdminLocationCategories() {
  const { styles: adminStyles, colors, isDarkMode } = useAdminThemeContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [busyId, setBusyId] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  const [denyingId, setDenyingId] = useState(null);

  const searchPlaceholderColor = getAdminSearchPlaceholderColor(isDarkMode);

  useEffect(() => {
    setWebPlaceholderColor(DENY_REASON_INPUT_ID, searchPlaceholderColor);
  }, [searchPlaceholderColor]);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'locationCategorySuggestions'),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          const ta = a.requestedAt?.seconds ?? a.createdAt?.seconds ?? 0;
          const tb = b.requestedAt?.seconds ?? b.createdAt?.seconds ?? 0;
          return tb - ta;
        });
        setItems(rows);
        setLoading(false);
      },
      (err) => {
        console.error('locationCategorySuggestions', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    if (filterStatus === 'all') return items;
    return items.filter((x) => (x.status || 'pending') === filterStatus);
  }, [items, filterStatus]);

  const pillStyle = (active) => ({
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: active ? colors.accentGreen : colors.cardBgSubtle,
    borderWidth: active ? 0 : 1,
    borderColor: colors.cardBorder,
  });

  const pillTextStyle = (active) => ({
    color: active ? '#fff' : colors.textPrimary,
    fontWeight: '800',
    fontSize: 12,
  });

  const rowCardStyle = {
    backgroundColor: colors.cardBg,
    borderRadius: 10,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  };

  const approve = async (row) => {
    const auth = getAuth();
    setBusyId(row.id);
    try {
      await updateDoc(doc(db, 'locationCategorySuggestions', row.id), {
        status: 'approved',
        reviewedAt: serverTimestamp(),
        reviewedByUid: auth.currentUser?.uid || null,
      });
    } catch (e) {
      console.error(e);
      alert(e.message || 'Approve failed');
    } finally {
      setBusyId(null);
    }
  };

  const deny = async () => {
    if (!denyingId) return;
    const auth = getAuth();
    setBusyId(denyingId);
    try {
      await updateDoc(doc(db, 'locationCategorySuggestions', denyingId), {
        status: 'rejected',
        reviewedAt: serverTimestamp(),
        reviewedByUid: auth.currentUser?.uid || null,
        denyReason: denyReason.trim() || null,
      });
      setDenyingId(null);
      setDenyReason('');
    } catch (e) {
      console.error(e);
      alert(e.message || 'Deny failed');
    } finally {
      setBusyId(null);
    }
  };

  const filters = [
    { id: 'pending', label: 'Pending' },
    { id: 'approved', label: 'Approved' },
    { id: 'rejected', label: 'Denied' },
    { id: 'all', label: 'All' },
  ];

  return (
    <View style={{ width: '100%', maxWidth: 960, paddingHorizontal: 16, paddingBottom: 40 }}>
      <Text style={[adminStyles.sectionTitle, { marginBottom: 8 }]}>
        Location category suggestions
      </Text>
      <Text style={{ color: colors.textSecondary, fontSize: 13, marginBottom: 16, lineHeight: 20 }}>
        Users can suggest new categories from Tour Planner when adding a map location. Approve to add them to the
        public category list (alongside built-in categories like Venue, Hotel, etc.).
      </Text>

      <Text style={{ color: colors.textMuted, fontSize: 12, marginBottom: 12 }}>
        Built-in categories ({BASE_LOCATION_CATEGORIES.length}):{' '}
        {BASE_LOCATION_CATEGORIES.map((c) => c.label).join(' · ')}
      </Text>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {filters.map((f) => (
          <TouchableOpacity
            key={f.id}
            onPress={() => setFilterStatus(f.id)}
            style={pillStyle(filterStatus === f.id)}
          >
            <Text style={pillTextStyle(filterStatus === f.id)}>{f.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {loading ? (
        <ActivityIndicator size="large" color={colors.accentGreen} />
      ) : (
        <ScrollView style={{ maxHeight: 520 }}>
          {filtered.length === 0 ? (
            <Text style={adminStyles.emptyStateText}>No items in this filter.</Text>
          ) : (
            filtered.map((row) => (
              <View key={row.id} style={rowCardStyle}>
                <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <Text style={{ fontSize: 22 }}>{row.icon || '📌'}</Text>
                  <Text style={{ color: colors.textPrimary, fontWeight: '800', fontSize: 16, flex: 1 }}>{row.label}</Text>
                  <Text
                    style={{
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: '700',
                      paddingHorizontal: 8,
                      paddingVertical: 4,
                      borderRadius: 6,
                      overflow: 'hidden',
                      backgroundColor:
                        row.status === 'approved' ? '#27ae60' : row.status === 'rejected' ? '#c0392b' : '#f39c12',
                    }}
                  >
                    {(row.status || 'pending').toUpperCase()}
                  </Text>
                </View>
                <Text style={{ color: colors.textSecondary, fontSize: 12, marginTop: 6 }}>
                  Doc ID (saved as category on locations):{' '}
                  <Text style={{ color: colors.accentGreen }}>{row.id}</Text>
                </Text>
                <Text style={{ color: colors.textSecondary, fontSize: 12, marginTop: 4 }}>
                  Requested: {formatTime(row.requestedAt)} ·{' '}
                  {row.requestedByEmail || row.requestedByUid || 'unknown'}
                </Text>
                {row.reviewedAt ? (
                  <Text style={{ color: colors.textMuted, fontSize: 11, marginTop: 4 }}>
                    Reviewed: {formatTime(row.reviewedAt)}
                  </Text>
                ) : null}
                {row.denyReason ? (
                  <Text style={{ color: '#c0392b', fontSize: 12, marginTop: 6 }}>
                    Deny reason: {row.denyReason}
                  </Text>
                ) : null}

                {(row.status === 'pending' || !row.status) && (
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                    <TouchableOpacity
                      onPress={() => approve(row)}
                      disabled={busyId === row.id}
                      style={{
                        paddingVertical: 10,
                        paddingHorizontal: 16,
                        backgroundColor: '#27ae60',
                        borderRadius: 8,
                        opacity: busyId === row.id ? 0.6 : 1,
                      }}
                    >
                      <Text style={{ color: '#fff', fontWeight: '800' }}>Approve</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => {
                        setDenyingId(row.id);
                        setDenyReason('');
                      }}
                      disabled={busyId === row.id}
                      style={{
                        paddingVertical: 10,
                        paddingHorizontal: 16,
                        backgroundColor: '#c0392b',
                        borderRadius: 8,
                        opacity: busyId === row.id ? 0.6 : 1,
                      }}
                    >
                      <Text style={{ color: '#fff', fontWeight: '800' }}>Deny</Text>
                    </TouchableOpacity>
                  </View>
                )}

                {denyingId === row.id && (
                  <View style={{ marginTop: 12 }}>
                    <Text style={{ color: colors.textSecondary, fontSize: 12, marginBottom: 6 }}>
                      Optional reason
                    </Text>
                    <TextInput
                      nativeID={DENY_REASON_INPUT_ID}
                      value={denyReason}
                      onChangeText={setDenyReason}
                      placeholder="Reason for denial"
                      placeholderTextColor={searchPlaceholderColor}
                      style={{
                        backgroundColor: colors.inputBg,
                        borderRadius: 8,
                        padding: 10,
                        color: colors.inputText,
                        marginBottom: 8,
                        borderWidth: 1,
                        borderColor: colors.inputBorder,
                      }}
                    />
                    <View style={{ flexDirection: 'row', gap: 8 }}>
                      <TouchableOpacity
                        onPress={deny}
                        style={{ paddingVertical: 8, paddingHorizontal: 14, backgroundColor: '#c0392b', borderRadius: 8 }}
                      >
                        <Text style={{ color: '#fff', fontWeight: '700' }}>Confirm deny</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={() => {
                          setDenyingId(null);
                          setDenyReason('');
                        }}
                        style={{
                          paddingVertical: 8,
                          paddingHorizontal: 14,
                          backgroundColor: colors.cardBgSubtle,
                          borderRadius: 8,
                          borderWidth: 1,
                          borderColor: colors.cardBorder,
                        }}
                      >
                        <Text style={{ color: colors.textPrimary, fontWeight: '700' }}>Cancel</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
              </View>
            ))
          )}
        </ScrollView>
      )}
    </View>
  );
}
